import React, { useState } from "react";
import API_BASE_URL from "./RestAPI";
import { showSuccessToast, showErrorToast } from "../constants/ToastService.js";
import { FiBriefcase, FiUser, FiEye, FiEyeOff, FiCheckCircle, FiCopy, FiSettings } from "react-icons/fi";
import "./ClientOnboarding.css";

const EMPTY = {
    company_name: "", short_name: "", city_name: "", state_name: "",
    address: "", gst_number: "", mobile_number: "",
    admin_name: "", admin_email: "", admin_mobile: "", admin_username: "", admin_password: "",
    // Basic configuration (applied to the new company's Configuration row)
    employee_code_type: 1,
    enable_live_support_sprint: false, enable_release_approval: false, enable_qa_time_tracking: false,
};

const ClientOnboarding = () => {
    const [form, setForm] = useState(EMPTY);
    const [showPwd, setShowPwd] = useState(false);
    const [saving, setSaving] = useState(false);
    const [result, setResult] = useState(null); // {company_name, short_name, admin_username, admin_member_code}

    const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const REQUIRED = ["company_name", "short_name", "city_name", "state_name", "admin_name", "admin_email", "admin_username", "admin_password"];

    const handleSubmit = async () => {
        const missing = REQUIRED.filter((k) => !String(form[k] || "").trim());
        if (missing.length) { showErrorToast("Please fill all required (*) fields"); return; }
        if (!/^\S+@\S+\.\S+$/.test(form.admin_email)) { showErrorToast("Enter a valid admin email"); return; }
        setSaving(true);
        try {
            const res = await fetch(`${API_BASE_URL}core/onboard-client/`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("authToken")}` },
                body: JSON.stringify(form),
            });
            const data = await res.json();
            if (res.ok && data.status === "success") {
                showSuccessToast("Client onboarded");
                setResult({ ...data.data, admin_password: form.admin_password });
            } else {
                showErrorToast(data.message || "Onboarding failed");
            }
        } catch (e) {
            showErrorToast("Onboarding failed. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    const onboardAnother = () => { setForm(EMPTY); setResult(null); setShowPwd(false); };

    const copyCreds = () => {
        const text = `FixDesk login\nClient: ${result.company_name}\nURL: ${window.location.origin}\nUsername: ${result.admin_username}\nPassword: ${result.admin_password}`;
        navigator.clipboard?.writeText(text).then(() => showSuccessToast("Credentials copied")).catch(() => {});
    };

    if (result) {
        return (
            <div className="onb-page">
                <div className="onb-success">
                    <FiCheckCircle size={42} className="onb-success__icon" />
                    <h2>Client onboarded successfully</h2>
                    <p className="onb-success__sub">{result.company_name} (code {result.short_name}) is now set up. Share these login credentials with the client admin.</p>
                    <div className="onb-cred-card">
                        <div className="onb-cred-row"><span>Company</span><strong>{result.company_name}</strong></div>
                        <div className="onb-cred-row"><span>Admin member code</span><strong>{result.admin_member_code}</strong></div>
                        <div className="onb-cred-row"><span>Username</span><strong>{result.admin_username}</strong></div>
                        <div className="onb-cred-row"><span>Password</span><strong>{result.admin_password}</strong></div>
                    </div>
                    <div className="onb-success__actions">
                        <button className="onb-btn onb-btn--outline" onClick={copyCreds}><FiCopy size={13} /> Copy credentials</button>
                        <button className="onb-btn onb-btn--primary" onClick={onboardAnother}>Onboard another client</button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="onb-page">
            <div className="onb-header">
                <h1 className="onb-header__title">Client Onboarding</h1>
                <p className="onb-header__sub">Provision a new client company on FixDesk with its admin login. Statuses, roles and other masters can be configured afterwards in Masters.</p>
            </div>

            <div className="onb-grid">
                {/* ── Company ── */}
                <div className="onb-card">
                    <div className="onb-card__title"><FiBriefcase size={15} /> Company Details</div>
                    <div className="onb-fields">
                        <label className="onb-field onb-field--wide">
                            <span>Company Name <i>*</i></span>
                            <input value={form.company_name} onChange={(e) => set("company_name", e.target.value)} placeholder="e.g. Acme Health Pvt Ltd" />
                        </label>
                        <label className="onb-field">
                            <span>Short Code <i>*</i></span>
                            <input value={form.short_name} maxLength={5} onChange={(e) => set("short_name", e.target.value.toUpperCase())} placeholder="ACME" />
                            <small>Up to 5 chars — used in ticket/member codes</small>
                        </label>
                        <label className="onb-field">
                            <span>Mobile Number</span>
                            <input value={form.mobile_number} onChange={(e) => set("mobile_number", e.target.value.replace(/\D/g, ""))} maxLength={10} placeholder="9876543210" />
                        </label>
                        <label className="onb-field">
                            <span>City <i>*</i></span>
                            <input value={form.city_name} onChange={(e) => set("city_name", e.target.value)} placeholder="Chennai" />
                        </label>
                        <label className="onb-field">
                            <span>State <i>*</i></span>
                            <input value={form.state_name} onChange={(e) => set("state_name", e.target.value)} placeholder="Tamil Nadu" />
                        </label>
                        <label className="onb-field">
                            <span>GST Number</span>
                            <input value={form.gst_number} onChange={(e) => set("gst_number", e.target.value.toUpperCase())} maxLength={15} placeholder="33AAAAA0000A1Z5" />
                        </label>
                        <label className="onb-field onb-field--wide">
                            <span>Address</span>
                            <textarea value={form.address} onChange={(e) => set("address", e.target.value)} rows={2} placeholder="Registered address" />
                        </label>
                    </div>
                </div>

                {/* ── Admin login ── */}
                <div className="onb-card">
                    <div className="onb-card__title"><FiUser size={15} /> Client Admin Login</div>
                    <div className="onb-fields">
                        <label className="onb-field onb-field--wide">
                            <span>Admin Name <i>*</i></span>
                            <input value={form.admin_name} onChange={(e) => set("admin_name", e.target.value)} placeholder="Full name of the client admin" />
                        </label>
                        <label className="onb-field">
                            <span>Admin Email <i>*</i></span>
                            <input type="email" value={form.admin_email} onChange={(e) => set("admin_email", e.target.value)} placeholder="admin@acme.com" />
                        </label>
                        <label className="onb-field">
                            <span>Admin Mobile</span>
                            <input value={form.admin_mobile} onChange={(e) => set("admin_mobile", e.target.value.replace(/\D/g, ""))} maxLength={10} placeholder="9876543210" />
                        </label>
                        <label className="onb-field">
                            <span>Username <i>*</i></span>
                            <input value={form.admin_username} onChange={(e) => set("admin_username", e.target.value.trim())} placeholder="acme_admin" />
                        </label>
                        <label className="onb-field">
                            <span>Password <i>*</i></span>
                            <div className="onb-pwd">
                                <input type={showPwd ? "text" : "password"} value={form.admin_password} onChange={(e) => set("admin_password", e.target.value)} placeholder="Set a password" />
                                <button type="button" onClick={() => setShowPwd((s) => !s)} title={showPwd ? "Hide" : "Show"}>{showPwd ? <FiEyeOff size={14} /> : <FiEye size={14} />}</button>
                            </div>
                        </label>
                    </div>
                    <p className="onb-note">The admin is created with a full-access <strong>Admin</strong> role for the new company. They can add more users/roles from Masters after logging in.</p>
                </div>

                {/* ── Basic Configuration ── */}
                <div className="onb-card">
                    <div className="onb-card__title"><FiSettings size={15} /> Basic Configuration</div>
                    <div className="onb-fields">
                        <label className="onb-field onb-field--wide">
                            <span>Employee Code Type</span>
                            <select value={form.employee_code_type} onChange={(e) => set("employee_code_type", Number(e.target.value))}>
                                <option value={1}>Employee-Wise (company code)</option>
                                <option value={2}>Designation-Wise (role prefix)</option>
                            </select>
                            <small>How member codes are generated for future users</small>
                        </label>
                        <div className="onb-field onb-field--wide">
                            <span>Feature Toggles</span>
                            <div className="onb-checks">
                                <label className="onb-check">
                                    <input type="checkbox" checked={form.enable_live_support_sprint} onChange={(e) => set("enable_live_support_sprint", e.target.checked)} />
                                    Live Support Sprint &amp; move-to-live workflow
                                </label>
                                <label className="onb-check">
                                    <input type="checkbox" checked={form.enable_release_approval} onChange={(e) => set("enable_release_approval", e.target.checked)} />
                                    Release-approval workflow
                                </label>
                                <label className="onb-check">
                                    <input type="checkbox" checked={form.enable_qa_time_tracking} onChange={(e) => set("enable_qa_time_tracking", e.target.checked)} />
                                    QA Time Tracking report
                                </label>
                            </div>
                        </div>
                    </div>
                    <p className="onb-note">Statuses, priorities and ticket types are configured by the client admin in Masters after login.</p>
                </div>
            </div>

            <div className="onb-footer">
                <button className="onb-btn onb-btn--ghost" onClick={onboardAnother} disabled={saving}>Reset</button>
                <button className="onb-btn onb-btn--primary" onClick={handleSubmit} disabled={saving}>{saving ? "Onboarding…" : "Onboard Client"}</button>
            </div>
        </div>
    );
};

export default ClientOnboarding;
